import { ActionType, JwtPayload, ScopeTypes } from "types/JWT";
import { atom, selector } from "recoil";
import { getDefaultPermissions } from "./userSelectedRole";
import { ConfigurationService } from "@adfenix/types";

export type Permissions = {
  [key in ScopeTypes | string]: ActionType[];
};

export const jwtPayload = atom<JwtPayload | undefined>({
  key: "authJwtPayload",
  default: undefined
});

export const getIsAuthenticated = selector({
  key: "authGetIsAuthenticated",
  get: ({ get }) => {
    const payload = get(jwtPayload);

    return Boolean(payload);
  }
});

export const getIsAdfenixToken = selector({
  key: "authGetIsAdfenixToken",
  get: ({ get }) => {
    const payload = get(jwtPayload);

    return payload?.tokenSource === "ADFENIX";
  }
});

export const getAgentId = selector({
  key: "authGetAgentId",
  get: ({ get }) => {
    const payload = get(jwtPayload);

    return payload?.agentId;
  }
});

export const userDomains = atom<string[]>({
  key: "authUserDomains",
  default: []
});

export const userSelectedHq = atom<
  ConfigurationService.HqAndDomainModel | undefined
>({
  key: "authSelectedHq",
  default: undefined
});

export const userSelectedOffice = atom<
  ConfigurationService.OfficeForTokenModel | undefined
>({
  key: "authSelectedOffice",
  default: undefined
});

export const getUserHqs = selector({
  key: "authGetUserHq",
  get: ({ get }) => {
    const payload = get(jwtPayload);

    return payload?.hqs ? payload?.hqs.split(",") : [];
  }
});

export const getUserOffices = selector({
  key: "authGetUserOffice",
  get: ({ get }) => {
    const payload = get(jwtPayload);

    return payload?.offices ? payload?.offices.split(",") : [];
  }
});

export const convertPermissionStringToObject = (permissions: string) => {
  const flatPermissionsArray = permissions ? permissions.split(",") : [];

  const scopedPermissionsArray = flatPermissionsArray.map(p =>
    p.split(":")
  ) as [ScopeTypes, ActionType][];

  const permissionsObject = scopedPermissionsArray.reduce<Permissions>(
    (acc, curr) => {
      const currentScope = acc[curr[0]] ?? [];

      acc[curr[0]] = [...currentScope, curr[1]];

      return acc;
    },
    getDefaultPermissions()
  );

  return permissionsObject;
};

export const getUserPermissions = selector({
  key: "authGetUserPermissions",
  get: ({ get }) => {
    const payload = get(jwtPayload);

    if (!payload) return undefined;

    return convertPermissionStringToObject(payload.permissions);
  }
});

export const genericContactUsMessage = atom<string>({
  key: "genericContactUsMessage",
  default: "login.supportEmail"
});
